/**
 * Client portal layout: mobile-first and calm — the branded header (logo,
 * client name, language, light / dark, sign out), a small top nav, the page,
 * and the studio's contact. No studio sidebar or studio navigation. An
 * account not linked to a client yet sees "contact the studio" instead.
 */

import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { PageTransition } from '@/components/motion';
import { PortalHeader } from '@/features/portal/components/PortalHeader';
import { PortalNav } from '@/features/portal/components/PortalNav';
import { PortalNotLinked } from '@/features/portal/components/PortalNotLinked';
import { usePortalMe } from '@/features/portal/hooks/usePortalQueries';
import { isPortalNotLinked } from '@/features/portal/portalNotLinked';

import { ImpersonationBanner } from './ImpersonationBanner';

/** Both contacts, or only the one the studio has filled in. */
const footerKey = (email: string, phone: string) => {
  if (email && phone) return 'layout.footer';
  return email ? 'layout.footerEmail' : 'layout.footerPhone';
};

function PortalFooter({ email, phone }: { email: string; phone: string }) {
  const { t } = useTranslation('portal');
  if (!email && !phone) return null;
  return (
    <footer className="mx-auto w-full max-w-5xl px-4 pb-8 pt-2 text-center text-xs text-muted-foreground sm:px-6">
      {t(footerKey(email, phone), { email, phone })}
    </footer>
  );
}

export function PortalLayout() {
  const location = useLocation();
  const { data: me, error } = usePortalMe();
  const notLinked = isPortalNotLinked(error);
  return (
    // The app shell pins html/body/#root to the viewport; the portal scrolls inside its own container.
    <div className="flex h-full min-h-0 flex-col overflow-y-auto bg-background">
      <ImpersonationBanner />
      <header className="sticky top-0 z-30 border-b border-border/60 bg-card/85 backdrop-blur supports-[backdrop-filter]:bg-card/70">
        <div className="mx-auto w-full max-w-5xl px-4 sm:px-6">
          <PortalHeader clientName={me?.client.displayName ?? null} />
          {notLinked ? null : <PortalNav />}
        </div>
      </header>
      <main className="mx-auto w-full min-w-0 max-w-5xl flex-1 px-4 py-6 sm:px-6">
        {notLinked ? (
          <PortalNotLinked />
        ) : (
          <PageTransition key={location.pathname}>
            <Outlet />
          </PageTransition>
        )}
      </main>
      <PortalFooter email={me?.studio.email ?? ''} phone={me?.studio.phone ?? ''} />
    </div>
  );
}
